// client/src/App.js
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { HomeScreen } from './components/HomeScreen';
import AnnouncementsHome from './components/AnnouncementsHome';
import { TeamsScreen } from './components/TeamsScreen';
import { TeamDetailLayout } from './components/TeamDetailLayout';
import { ScheduleScreen } from './components/ScheduleScreen';
import { TeamScreen } from './components/TeamScreen';
import { PracticeScreen } from './components/PracticeScreen';
import { KitchenSink } from './components/KitchenSink';
import { AppChromeLayout } from './components/shell/AppChromeLayout';
import { OrgProvider } from './lib/OrgProvider';
import AnnouncementForm from './components/AnnouncementForm';
import './App.css';
import { ToastProvider } from './lib/ToastProvider';

function App() {
  return (
    // ToastProvider sits OUTSIDE OrgProvider so toast state survives any
    // OrgProvider remount (the "loading flash on Home → Teams → Home"
    // deferred item would wipe an inner ToastProvider's queue mid-flight).
    // Toasts don't depend on /me — the imperative API works before,
    // during, and after auth resolution.
    <ToastProvider>
      <OrgProvider>
        <AppShell />
      </OrgProvider>
    </ToastProvider>
  );
}

// Split out so AppShell can use hooks that depend on OrgProvider being
// mounted above it. (Calling useOrg() in App() directly would throw,
// because App is what mounts OrgProvider.)
//
// AppShell no longer renders navigation itself — that moved into
// AppChromeLayout, which wraps only the app-level routes as a layout
// route. Team-detail routes have their own chrome (PfTopBar + PfTabBar)
// and kitchen-sink is deliberately bare.
function AppShell() {
  return (
    <Routes>
      {/* App-level routes — wrapped in AppChromeLayout (top bar + drawer) */}
      <Route element={<AppChromeLayout />}>
        {/* AnnouncementsHome is the new "/" — app-level home showing
            active org's announcements across all scopes. */}
        <Route path="/" element={<AnnouncementsHome />} />
        <Route path="/teams" element={<TeamsScreen />} />
        {/* HomeScreen moved to /whoami as a dev diagnostic for the raw
            /me response. Delete this route once we no longer need it. */}
        <Route path="/whoami" element={<HomeScreen />} />
         <Route path="/announcements/new" element={<AnnouncementForm />} />
      </Route>

      {/* Team detail — its own chrome (PfTopBar + PfTabBar), so it
          sits OUTSIDE AppChromeLayout to avoid double headers. */}
      <Route path="/teams/:teamId" element={<TeamDetailLayout />}>
        <Route index element={<Navigate to="schedule" replace />} />
        <Route path="schedule" element={<ScheduleScreen />} />
        <Route path="team" element={<TeamScreen />} />
        <Route path="practice" element={<PracticeScreen />} />
      </Route>

      {/* Kitchen-sink — internal design system reference. Deliberately
          bare (no app chrome) so primitives render in isolation. */}
      <Route path="/kitchen-sink" element={<KitchenSink />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function NotFound() {
  return (
    <div style={{ padding: 20, fontFamily: 'sans-serif' }}>
      <h2>Page not found</h2>
      <p>
        <Link to="/">Go home</Link>
      </p>
    </div>
  );
}

export default App;